import { uuid } from '../../../shared/lib/uuid';
import { getCookieValue } from 'lib/util';
import { appUI } from 'lib/app-ui';
import EventTracker from 'lib/event-tracking';

class MapCollabClient {

  constructor({access, config}) {
    if (!window) {
      throw new Error("Must be called from browser");
    }
    this.retryNum = 0;
    this.access = access;
    this.config = config;
    // WebSocket readyState constants
    this.CONNECTING = 0;
    this.OPEN = 1;
    this.CLOSING = 2;
    this.CLOSED = 3;
    // max time to wait before auto-reloading page after app version update
    this.MAX_RELOAD_TIME = 30 * 1000;
    // set to true if we received and acted on an app version update
    this.reloadActionTriggered = null;

    this.setupReloadBackoff();

    this.connectScheduled = false;
    this.pingInterval = false;
    this.handlers = {};
    this.pendingOperations = {};

    this.lastPong = Date.now();
    this.currentSeq = null;
    this.setHandler('pong', async mess => {
      this.lastPong = Date.now();
      this.currentSequence = mess.currentSequence;
      if (this.triggerAppRefresh(mess) && this.triggerReloadAction('auto_refresh')) {
        this.reloadActionTriggered = 'auto_refresh';

        this.stopPinging();
        // if tab isn't visible, wait until it comes into focus
        if (!appUI.isWindowVisible()) {
          appUI.setRefreshOnVisibilityChange(true);
          return;
        }
        this.handlers.alert({
          content: 'Plectica is updating, please stand by…',
        });
        if (!this.waitingToReload) {
          const appVersion1 = this.config.app_version_tiers ? Object.values(this.config.app_version_tiers).join('.') : this.config.app_version;
          const appVersion2 = mess.app_version_tiers ? Object.values(mess.app_version_tiers).join('.') : mess.app_version;
          console.log(`reloading to upgrade from app version ${appVersion1} to ${appVersion2} in ${this.reloadDelay}ms`);
          appUI.setViewOnly();
          setTimeout(() => {
            // keep count of how often we've reloaded the page automatically
            localStorage.setItem(this.reloadCounterKey, JSON.stringify({
              count: this.reloadCounter + 1,
              timestamp: Date.now()
            }));
            window.location.reload();
          }, this.reloadDelay);
          this.waitingToReload = true;
        }
      } else if (this.triggerUserPrompt(mess.app_version_tiers, 'hard_prompt') && this.triggerReloadAction('hard_prompt')) {
        this.reloadActionTriggered = 'hard_prompt';
        this.stopPinging();
        // if tab isn't visible, auto-refresh when it becomes visible
        if (!appUI.isWindowVisible()) {
          appUI.setRefreshOnVisibilityChange(true);
          return;
        }
        appUI.setViewOnly();
        this.handlers.alert({
          content: 'Plectica has updated. Please refresh to continue working',
          type: 'Warning',
          link: {
            label: 'Reload Page',
            url: window.location.href,
          }
        });
      } else if (this.triggerUserPrompt(mess.app_version_tiers, 'soft_prompt') && this.triggerReloadAction('soft_prompt')) {
        this.reloadActionTriggered = 'soft_prompt';
        if (!appUI.isWindowVisible()) {
          this.stopPinging();
          appUI.setRefreshOnVisibilityChange(true);
          return;
        }
        this.handlers.alert({
          content: 'Plectica has new updates.  Please refresh to get the latest version',
          type: 'Info',
          link: {
            label: 'Reload Page',
            url: window.location.href,
          }
        });
      }
    });


    this.setHandler('operation-response', async mess => {
      if (mess.status == 'failure') {
        if (mess.error && mess.error == 'permission denied') {
          console.error('permission denied', mess);
          appUI.setViewOnly();
          EventTracker.track({
            category: 'Warning',
            action: 'Operation Failure',
            label: 'Permission Denied',
          });
        } else {
          appUI.stopTheWorldError({ skipViewOnlyCheck: true });
          EventTracker.track({
            category: 'Warning',
            action: 'Operation Failure',
            label: 'Operation Failed',
          });
        }
      }
      const resolve = this.pendingOperations[mess.opId];
      delete this.pendingOperations[mess.opId];
      if (resolve) {
        resolve(mess);
      }
    });

    if (window.location.port) {
      if (window.config.uri && window.config.uri.protocol == 'https') {
        this.url = `wss://${window.location.hostname}:${window.location.port}/map/collab`;
      } else {
        this.url = `ws://${window.location.hostname}:${window.location.port}/map/collab`;
      }
    } else {
      if (window.config.uri && window.config.uri.protocol == 'https') {
        this.url = `wss://${window.location.hostname}/map/collab`;
      } else {
        this.url = `ws://${window.location.hostname}/map/collab`;
      }
    }
    this.connect();
  }

  setHistoryUI(hui) {
    this.hui = hui;
  };

  // if the new action being triggered is of lower precedence than the prior one triggered, then ignore it
  triggerReloadAction(action) {
    if (action == 'soft_prompt') {
      // if we want to trigger a soft prompt,
      // and we already triggered an action of equal or higher precendence, ignore it
      if (this.reloadActionTriggered == 'soft_prompt' ||
          this.reloadActionTriggered == 'hard_prompt' ||
          this.reloadActionTriggered == 'auto_refresh') {
        return false;
      }
    } else if (action == 'hard_prompt') {
      if (this.reloadActionTriggered == 'hard_prompt' ||
          this.reloadActionTriggered == 'auto_refresh') {
        return false;
      }
    } else if (action == 'auto_refresh') {
      if (this.reloadActionTriggered == 'auto_refresh') {
        return false;
      }
    }
    return true;
  }


  /*
   set this.reloadDelay based on number of times we've recently reloaded the page

   track in local storage how many times the app was auto-refreshed recently
   and back off if there's a refresh loop in progress
  */
  setupReloadBackoff() {
    this.waitingToReload = false;
    this.reloadCounterKey = 'appReloadCount';
    const json = localStorage.getItem(this.reloadCounterKey);
    let reloadCounterPayload = {};
    try {
      reloadCounterPayload = Boolean(json) ? JSON.parse(json) : {};
    } catch (e) {
      console.log('error in reload backoff',e);
    }
    const timestamp = reloadCounterPayload ? reloadCounterPayload.timestamp : 0;

    // setup reload delay based on number of times the page has been reloaded automatically
    // expire backoff after 5 minutes
    if (timestamp && Date.now() - timestamp > 5 * 60 * 1000) {
      localStorage.removeItem(this.reloadCounterKey);
      this.reloadCounter = 0;
    } else {
      this.reloadCounter = reloadCounterPayload ? parseInt(reloadCounterPayload.count || 0) : 0;
    }
    // increase time exponentially after each refresh attempt
    // add a random number of seconds between 0 and 10 to avoid all browsers refreshing at once
    this.reloadDelay = 1000 * (Math.pow(2,this.reloadCounter)) + (Math.round(Math.random() * 10000));
    if (this.reloadDelay > this.MAX_RELOAD_TIME) {
      this.reloadDelay = this.MAX_RELOAD_TIME;
    }
  }

  // return true if we need to trigger a prompt for the user to refresh the page
  // (type can be 'hard_prompt' or 'soft_prompt')
  triggerUserPrompt(messVersion,type) {
    const configVersion = this.config.app_version_tiers;

    // if our operation message had no version, do nothing
    if (!messVersion || !messVersion[type]) {
      return false;
    }
    // if we're missing the version in our local config, trigger the prompt
    if (!configVersion[type]) {
      return true;
    }
    // if message version is greater than local version, trigger prompt
    if (configVersion[type] < messVersion[type]) {
      return true;
    }

    return false;
  }

  // return true if we need to trigger an automatic page refresh
  // handle backwards compatibility too
  triggerAppRefresh(mess) {
    if (!mess.app_version && !mess.app_version_tiers) {
      return false;
    }


    // backwards compatibility stuff:
    // if the old app version format is loaded in the app...
    if (this.config.app_version && !this.config.app_version_tiers) {

      // received old app version format in message...
      if (mess.app_version &&
         !mess.app_version_tiers &&
         (!this.config.app_version || (this.config.app_version < mess.app_version))) {
        return true;
      }
      // if received message with new app version format...
      if (mess.app_version_tiers) {
        return true;
      }
      return false;
    }


    /*
      trigger refresh if:
      * the app currently has the old format app version and a new format app version came through
      * the app_version.refresh in the app is less than the app_version.refresh that came through the latest ping
    */
    if (mess.app_version_tiers &&
        mess.app_version_tiers.auto_refresh &&
        (!this.config.app_version_tiers.auto_refresh ||
         (this.config.app_version_tiers.auto_refresh < mess.app_version_tiers.auto_refresh))) {
      return true;
    }

    return false;
  }



  isConnecting() {
    return this.ws && this.ws.readyState == this.CONNECTING;
  }
  isOpen() {
    return this.ws && this.ws.readyState == this.OPEN;
  }
  isClosing() {
    return this.ws && this.ws.readyState == this.CLOSING;
  }
  isClosed() {
    return this.ws && this.ws.readyState == this.CLOSED;
  }

  connect() {
    this.clientId = uuid();
    this.clientSequence = 0;
    console.log('connecting to ', `${this.url}?clientId=${this.clientId}`);
    this.ws = new window.WebSocket(`${this.url}?clientId=${this.clientId}`);

    this.ws.onopen = e => {
      this.retryNum = 0;
      this.connectScheduled = false;
      console.log('connected: ', [window.user_id, this.clientId].join(':'));

      const ping = {
        type: 'ping',
        subscriptions: [
          `map.${window.map_id}.presence`,
          `map.${window.map_id}.mouse_move`,
          `map.${window.map_id}.operation`,
        ]
      };

      this.send(ping);

      if (this.pingInterval) {
        clearInterval(this.pingInterval);
      }
      this.pingInterval = setInterval(() => {
        this.send(ping);
        // if we haven't seen a pong in more than 15 seconds, we're likely
        // disconnected.
        if ((Date.now() - this.lastPong) > 15000) {
          console.error('closing stale websocket');
          this.pingInterval = clearInterval(this.pingInterval);
          this.ws.close(); // will fire reconnect
        }
      }, 500);

    };

    this.ws.onmessage = async e => {
      const message = JSON.parse(e.data);
      if (!message.type) {
        console.error(`message has no type: ${e.data}`);
      }
      if (this.handlers[message.type]) {

        // if receive a remote operation while in playback, tell the operation
        // handler not to emit UI update events, we're operating on a cloned
        // store and managing our own updates.
        const skipUpdates = this.hui && this.hui.inPlayback && (message.type === 'operation');

        await this.handlers[message.type](message, skipUpdates);

        if (message.type === 'operation' && message.opId) {
          this.sendOperationReceivedMessage({ operationId: message._sequence });
        }

      } else {
        console.log('non-handled message: ', message);
      }
    };

    this.ws.onclose = e => {
      console.error('close: ', e);
      this.connectScheduled = clearTimeout(this.connectScheduled);
      this.reconnect();
    };
    this.ws.onerror = e => {
      console.error('error: ', e);
    };
  }

  // for testing
  stopPinging() {
    clearInterval(this.pingInterval);
  }

  reconnect() {
    if (this.loggedOut) {
      return;
    }
    if (!window.user && !getCookieValue('visitor')) {
      console.error("No user or visitor id - can't re-establish websocket connection.");
      this.stopPinging();
      return;
    }
    if (this.isConnecting()) {
      console.log('already connecting');
      return;
    }
    if (this.isOpen()) {
      console.log('already connected');
      return;
    }
    if (this.connectScheduled) {
      console.log('connect already scheduled');
      return;
    }

    // max out at 5 second retries
    if (this.retryNum < 5) {
      this.retryNum++;
    }

    console.log(`scheduling connect in ${this.retryNum} seconds`);
    this.connectScheduled = setTimeout(() => {
      this.connect();
    }, this.retryNum * 1000);
  }

  send(obj) {
    if (!obj || (typeof (obj) !== 'object')) {
      throw new Error("MapCollaboration.send needs an object");
    }
    if (!obj.type) {
      throw new Error("MapCollaboration.send message has no 'type'");
    }
    let validTypes = [
      'ping',
      'presence',
      'mouse_move',
      'operation',
      'node-count',
      'receipt',
    ];

    // if logged out, only send ping and presence
    if (!window.user) {
      validTypes = [
        'ping',
        'presence',
      ];

      if (!validTypes.includes(obj.type)) {
        return;
      }
    }

    if (!validTypes.includes(obj.type)) {
      throw new Error(`wrong type ${obj.type} for send`);
    }

    obj.mapId = window.map_id;
    obj.editKey = this.access.editKey;
    obj.clientId = this.clientId;

    if (this.isOpen()) {
      if (obj.type === 'operation') {
        obj.clientSequence = this.clientSequence++;
      }
      this.ws.send(JSON.stringify(obj));
      if (obj.type === 'operation') {
        if (this.hui && obj.operation !== 'getOperationsAfterSequence') {
          // add some bits for recent activity
          obj.userId = window.user_id;
          obj.user = { id: window.user_id };
          obj.createdTime = Date.now();
          try {
            this.hui.operationCollector(obj);
          } catch (e) {
            console.log(`couldn't collect ${obj.operation}: ${e}`);
          }
        }
        return new Promise((resolve, reject) => {
          this.pendingOperations[obj.opId] = resolve;
        });
      }
    } else {
      // ensure a reconnect attempt is queued up.
      this.reconnect();
      console.error('could not send!: ', obj.type);
      if (obj.type === 'operation') {
        return Promise.resolve({ status: 'failure', error: 'disconnected' });
      }
    }
  }

  setHandler(type, handler) {
    if (!type || !handler) {
      throw new Error('usage: setHandler(type, handler)');
    }
    const validTypes = [
      'pong',
      'presence',
      'mouse_move',
      'operation',
      'operation-response',
      'logout',
      'alert',
      'node-count',
    ];
    if (!validTypes.includes(type)) {
      throw new Error(`wrong type ${type} for setHandler`);
    }
    if (typeof (handler) !== 'function') {
      throw new Error('handler must be a function');
    }
    this.handlers[type] = handler;
  }


  /*
    after an operation from a collaborator has been received and rendered,
    send a message that the operation was received
    (used for notifications / catch me up)
  */
  sendOperationReceivedMessage({ operationId }) {
    if (!window.user) return;

    this.send({
      type: 'receipt',
      operationId,
      mapId: window.map_id,
    });
  }

}

module.exports = MapCollabClient;



// WEBPACK FOOTER //
// ./starling/src/lib/map_collab_client.js